import React, { useState, useEffect, useMemo } from "react";
import VisorPluginPlaceholder from "../../_visor/components/canvas/VisorPluginPlaceholder";
import QuizStatusLine from "../../_visor/components/canvas/QuizStatusLine";
import { contentI18n as i18n } from "../../common/content_language";
import { setRgbaAlpha, shuffle, getButtonText, showVisorToast } from "../../common/common_tools";
import { getTrueFalseActionButtonStyle, getTrueFalseOptionVars } from "./trueFalseButtonStyle";

const TrueFalseVisor = (props) => {
  const [indexes, setIndexes] = useState(() =>
    generateIndexes(props.state.nBoxes, props.state.shuffle)
  );

  const { state, props: componentProps } = props;

  const {
    exercises,
    boxes,
    toolbars,
    id,
    setAnswer,
    tryAgainHandler,
    seeAnswerHandler,
    submitPage,
  } = componentProps;

  // Memoized values
  const attempted = exercises?.attempted || false;
  const score = useMemo(() => {
    let calculatedScore = exercises.score || 0;
    calculatedScore = Math.round(calculatedScore * 100) / 100;

    return exercises.weight === 0
      ? i18n.t("TrueFalse.notCount")
      : `${calculatedScore}/${exercises.weight}`;
  }, [exercises.score, exercises.weight]);

  const isCorrect = useMemo(() => {
    return (
      attempted &&
      JSON.stringify(exercises.correctAnswer) === JSON.stringify(exercises.currentAnswer)
    );
  }, [attempted, exercises.correctAnswer, exercises.currentAnswer]);

  // Feedback containers
  const trueFeedbackContainer = boxes[id]?.sortableContainers?.["sc-TrueFeedback"];
  const trueFeedbackToolbar = trueFeedbackContainer?.children?.[0]
    ? toolbars[trueFeedbackContainer.children[0]]
    : null;

  const falseFeedbackContainer = boxes[id]?.sortableContainers?.["sc-FalseFeedback"];
  const falseFeedbackToolbar = falseFeedbackContainer?.children?.[0]
    ? toolbars[falseFeedbackContainer.children[0]]
    : null;

  // Helper function to fix img/svg structure
  const fixImgSvgStructure = (text) => {
    if (!text || typeof text !== "string") return text;

    let decoded = text;
    try {
      if (text.includes("%3C") || text.includes("%3E")) {
        decoded = decodeURIComponent(text);
      }
    } catch (e) {
      console.error("Error decoding text:", e);
    }

    // Replace img tags containing svg
    decoded = decoded.replace(/<img\s*([^>]*?)>([\s\S]*?)<\/img>/gi, (match, attrs, content) => {
      if (content?.trim()?.includes("<svg")) {
        let spanAttrs = attrs?.trim() || "";

        if (spanAttrs.includes("style=")) {
          spanAttrs = spanAttrs.replace(
            /style\s*=\s*["']([^"']*)["']/i,
            (styleMatch, styleContent) => {
              const cleanStyle = styleContent.trim().replace(/;\s*$/, "");
              return `style="${cleanStyle};display:inline-block;"`;
            }
          );
        } else {
          spanAttrs = `${spanAttrs ? spanAttrs + " " : ""}style="display:inline-block;"`;
        }

        return `<span ${spanAttrs}>${content}</span>`;
      }
      return match;
    });

    // Replace img tags followed by svg
    decoded = decoded.replace(/<img\s*([^>]*?)>\s*<svg/gi, (match, attrs) => {
      let spanAttrs = attrs?.trim() || "";

      if (spanAttrs.includes("style=")) {
        spanAttrs = spanAttrs.replace(
          /style\s*=\s*["']([^"']*)["']/i,
          (styleMatch, styleContent) => {
            const cleanStyle = styleContent.trim().replace(/;\s*$/, "");
            return `style="${cleanStyle};display:inline-block;"`;
          }
        );
      } else {
        spanAttrs = `${spanAttrs ? spanAttrs + " " : ""}style="display:inline-block;"`;
      }

      return `<span ${spanAttrs}><svg`;
    });

    decoded = decoded.replace(/<\/svg>[\s\n]*<\/img>/gi, "</svg></span>");

    if (text.includes("%3C") || text.includes("%3E")) {
      try {
        return encodeURIComponent(decoded);
      } catch (e) {
        return decoded;
      }
    }

    return decoded;
  };

  // Check feedback messages
  const checkFeedbackMessage = (feedbackText, defaultMsg) => {
    if (!feedbackText) return { isEmpty: true, isDefault: false };

    const isDefault =
      feedbackText === defaultMsg ||
      feedbackText === encodeURI(defaultMsg) ||
      feedbackText === encodeURI(defaultMsg + "\n") ||
      feedbackText.includes(i18n.t("MultipleChoice.TrueFeedbackMsg")) ||
      feedbackText.includes(i18n.t("MultipleChoice.FalseFeedbackMsg"));

    return { isDefault };
  };

  const trueFeedbackText = fixImgSvgStructure(trueFeedbackToolbar?.state?.__text || "");
  const defaultTrueFeedbackMsg = `<p>${i18n.t("MultipleChoice.TrueFeedbackMsg")}</p>`;
  const { isDefault: isDefaultTrueFeedbackMsg } = checkFeedbackMessage(
    trueFeedbackText,
    defaultTrueFeedbackMsg
  );

  const checkEmptyTrueFeedback =
    (!trueFeedbackContainer?.children ||
      trueFeedbackContainer.children.length === 0 ||
      trueFeedbackToolbar?.state?.__text === `<p>${i18n.t("text_here")}</p>` ||
      trueFeedbackToolbar?.state?.__text === encodeURI(`<p>${i18n.t("text_here")}</p>`) ||
      trueFeedbackToolbar?.state?.__text === encodeURI(`<p>${i18n.t("text_here")}</p>\n`)) &&
    !isDefaultTrueFeedbackMsg;

  const falseFeedbackText = fixImgSvgStructure(falseFeedbackToolbar?.state?.__text || "");
  const defaultFalseFeedbackMsg = `<p>${i18n.t("MultipleChoice.FalseFeedbackMsg")}</p>`;
  const { isDefault: isDefaultFalseFeedbackMsg } = checkFeedbackMessage(
    falseFeedbackText,
    defaultFalseFeedbackMsg
  );

  const checkEmptyFalseFeedback =
    (!falseFeedbackContainer?.children ||
      falseFeedbackContainer.children.length === 0 ||
      falseFeedbackToolbar?.state?.__text === `<p>${i18n.t("text_here")}</p>` ||
      falseFeedbackToolbar?.state?.__text === encodeURI(`<p>${i18n.t("text_here")}</p>`) ||
      falseFeedbackToolbar?.state?.__text === encodeURI(`<p>${i18n.t("text_here")}</p>\n`)) &&
    !isDefaultFalseFeedbackMsg;

  const quizColor = state.buttonBackgroundColor || state.quizColor || "rgba(237, 233, 254, 1)";
  const showFeedback = attempted && state.showFeedback;
  // En az bir kutucuğu işaretlenmiş mi? Sayfa geneli gönderim, dokunulmamış
  // soruları da "attempted" yapar; durum satırını yalnızca cevaplananlarda göster.
  const answered =
    Array.isArray(exercises.currentAnswer) &&
    exercises.currentAnswer.some((v) => v === "true" || v === "false");

  const isTryAgainMode = [
    "tryAgain",
    i18n.t("MultipleChoice.tryAgain"),
    "Try Again",
    "Tekrar Dene",
  ].includes(state.buttons);
  // After a check, always colour the learner's OWN pick (green if right, red if
  // wrong). The unpicked correct answer is only REVEALED when NOT in tryAgain
  // mode — tryAgain must not hand the answer over between attempts.
  const showAnswerStyles = attempted;
  const revealCorrect = attempted && !isTryAgainMode;

  const buttonStyles = getTrueFalseActionButtonStyle(state);

  const handleAnswerClick = (index, value) => {
    if (exercises) {
      const nBoxes = Array(state.nBoxes).fill("");
      const newAnswer = nBoxes.map((ans, ind) =>
        ind === index ? value : exercises.currentAnswer[ind]
      );
      setAnswer(newAnswer);
    }
  };

  const handleShuffleIndexes = () => {
    if (state.shuffle) {
      setIndexes(generateIndexes(state.nBoxes, state.shuffle));
    }
  };

  const handleSubmit = () => {
    let allAnswered = true;
    for (let i = 0; i < state.nBoxes; i++) {
      if (
        !exercises.currentAnswer[i] ||
        (exercises.currentAnswer[i] !== "true" && exercises.currentAnswer[i] !== "false")
      ) {
        allAnswered = false;
        break;
      }
    }

    if (allAnswered) {
      submitPage(componentProps.currentView, id);
    } else {
      showVisorToast(i18n.t("MultipleChoice.answerAll"));
    }
  };

  const shouldShowTryAgainButton = () => {
    const tryAgainOptions = [
      "tryAgain",
      i18n.t("MultipleChoice.tryAgain"),
      "Try Again",
      "Tekrar Dene",
    ];

    return !isCorrect && attempted && tryAgainOptions.includes(state.buttons);
  };

  const shouldShowSeeAnswerButton = () => {
    const seeAnswerOptions = [i18n.t("MultipleChoice.seeAnswer"), "seeAnswer"];

    return !isCorrect && attempted && seeAnswerOptions.includes(state.buttons);
  };

  // Render answer rows
  const renderAnswerRows = () => {
    const content = [];

    for (let i = 0; i < state.nBoxes; i++) {
      const correct =
        showAnswerStyles &&
        !isTryAgainMode &&
        exercises.correctAnswer[indexes[i]] === exercises.currentAnswer[indexes[i]];
      const incorrect =
        showAnswerStyles &&
        !isTryAgainMode &&
        exercises.correctAnswer[indexes[i]] !== exercises.currentAnswer[indexes[i]];

      const current = exercises.currentAnswer[indexes[i]];
      const isTrueSelected = current === "true";
      const isFalseSelected = current === "false";

      const trueIsCorrect = exercises.correctAnswer[indexes[i]] === "true";
      const falseIsCorrect = exercises.correctAnswer[indexes[i]] === "false";

      // Tekrar ("Try Again") modunda hiç renk verilmez (ne yeşil ne kırmızı) —
      // cevap açığa çıkmasın; sonuç yalnızca buton altındaki durum satırında.
      // Diğer modlarda: doğru şık, kullanıcı seçtiyse VEYA reveal serbestse yeşil;
      // kullanıcının kendi yanlış seçimi kırmızı. Seçilmeyenler nötr kalır.
      const trueGreen =
        attempted && !isTryAgainMode && trueIsCorrect && (revealCorrect || isTrueSelected);
      const trueRed = attempted && !isTryAgainMode && isTrueSelected && !trueIsCorrect;
      const falseGreen =
        attempted && !isTryAgainMode && falseIsCorrect && (revealCorrect || isFalseSelected);
      const falseRed = attempted && !isTryAgainMode && isFalseSelected && !falseIsCorrect;

      const trueIcon = trueGreen ? "✓" : trueRed ? "✕" : "";
      const falseIcon = falseGreen ? "✓" : falseRed ? "✕" : "";

      content.push(
        <div
          key={indexes[i]}
          className={`row answerRow ${correct ? "correct" : ""} ${incorrect ? "incorrect" : ""}`}
        >
          <div className="col-xs-10">
            <VisorPluginPlaceholder
              {...componentProps}
              key={indexes[i] + 1}
              pluginContainer={`Answer${indexes[i]}`}
            />
          </div>
          <div className="tf-options">
            <div
              className={`tf-option ${isTrueSelected ? "selected " : ""}${
                trueGreen ? "tf-option-correct" : trueRed ? " tf-option-incorrect" : ""
              }`}
              style={getTrueFalseOptionVars(state, "true")}
              role="button"
              aria-pressed={isTrueSelected}
              aria-label={`${i18n.t("TrueFalse.Answer")} ${i + 1} ${state.trueText || i18n.t("TrueFalse.True")}`}
              onClick={() => {
                if (!attempted) {
                  handleAnswerClick(indexes[i], "true");
                }
              }}
            >
              <span className="tf-option-icon" aria-hidden="true">
                {trueIcon}
              </span>
              <span className="tf-option-label">{state.trueText || i18n.t("TrueFalse.True")}</span>
            </div>
            <div
              className={`tf-option ${isFalseSelected ? "selected " : ""}${
                falseGreen ? "tf-option-correct" : falseRed ? " tf-option-incorrect" : ""
              }`}
              style={getTrueFalseOptionVars(state, "false")}
              role="button"
              aria-pressed={isFalseSelected}
              aria-label={`${i18n.t("TrueFalse.Answer")} ${i + 1} ${state.falseText || i18n.t("TrueFalse.False")}`}
              onClick={() => {
                if (!attempted) {
                  handleAnswerClick(indexes[i], "false");
                }
              }}
            >
              <span className="tf-option-icon" aria-hidden="true">
                {falseIcon}
              </span>
              <span className="tf-option-label">
                {state.falseText || i18n.t("TrueFalse.False")}
              </span>
            </div>
          </div>
        </div>
      );
    }

    return content;
  };

  return (
    <div
      className={`exercisePlugin truefalsePlugin ${attempted ? "attempted" : ""} ${
        exercises.showFeedback ? "showFeedback" : ""
      }`}
      style={{ "--q-btn-hover-bg": state.buttonHoverBackgroundColor || "#ede9fe" }}
    >
      <div className="row">
        <div className="col-xs-12">
          <VisorPluginPlaceholder {...componentProps} pluginContainer="Question" />
        </div>
      </div>

      <div className="q-answer-list">{renderAnswerRows()}</div>

      <div className="q-feedback-slot">
        {isCorrect && !checkEmptyTrueFeedback && (
          <div className="row feedbackRow" style={{ display: showFeedback ? "block" : "none" }}>
            <div
              className="col-xs-12 feedback"
              role="status"
              aria-live="polite"
              style={{
                color: "black",
                borderColor: "#318533",
                backgroundColor: setRgbaAlpha("rgba(39,208,124,0.20)", 0.15),
              }}
            >
              <VisorPluginPlaceholder {...componentProps} pluginContainer="TrueFeedback" />
            </div>
          </div>
        )}

        {!isCorrect && !checkEmptyFalseFeedback && (
          <div className="row feedbackRow" style={{ display: showFeedback ? "block" : "none" }}>
            <div
              className="col-xs-12 feedback"
              role="status"
              aria-live="polite"
              style={{
                color: "black",
                borderColor: "#a31111",
                backgroundColor: setRgbaAlpha("rgba(213,26,26,0.20)", 0.15),
              }}
            >
              <VisorPluginPlaceholder {...componentProps} pluginContainer="FalseFeedback" />
            </div>
          </div>
        )}

        {isTryAgainMode && !state.showFeedback ? (
          <QuizStatusLine correct={isCorrect} visible={attempted && answered} />
        ) : null}
      </div>

      <div className="exerciseScore" style={{ color: quizColor }}>
        {score}
      </div>

      <div className="row">
        <button
          type="button"
          className="tryButton"
          style={{
            display: shouldShowTryAgainButton() ? "inline-block" : "none",
            ...buttonStyles,
          }}
          onClick={() => {
            handleShuffleIndexes();
            tryAgainHandler();
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = state.buttonHoverBackgroundColor || "#ede9fe";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = state.buttonBackgroundColor || "#ede9fe";
          }}
        >
          {getButtonText(state.tryAgainText, "MultipleChoice.tryAgain")}
        </button>

        <button
          type="button"
          className="tryButton"
          style={{
            display: shouldShowSeeAnswerButton() ? "inline-block" : "none",
            ...buttonStyles,
          }}
          onClick={seeAnswerHandler}
          onMouseEnter={(e) =>
            (e.target.style.backgroundColor = state.buttonHoverBackgroundColor || "#ede9fe")
          }
          onMouseLeave={(e) =>
            (e.target.style.backgroundColor = state.buttonBackgroundColor || "#ede9fe")
          }
        >
          {getButtonText(state.seeAnswerText, "MultipleChoice.seeAnswer")}
        </button>

        <button
          type="button"
          className="tryButton"
          style={{
            display: attempted ? "none" : "inline-block",
            ...buttonStyles,
          }}
          onClick={handleSubmit}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = state.buttonHoverBackgroundColor || "#ede9fe";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = state.buttonBackgroundColor || "#ede9fe";
          }}
        >
          {getButtonText(state.checkAnswerText, "MultipleChoice.checkAnswer")}
        </button>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
            .truefalsePlugin input[type="radio"] {
              background-color: transparent;
            }
            .truefalsePlugin input[type="radio"]:checked:after {
              background-color: rgb(49 161 72);
            }
          `,
        }}
      />
    </div>
  );
};

// Helper function
function generateIndexes(N, shouldShuffle) {
  const a = Array.from({ length: N }, (_, i) => i);
  return shouldShuffle ? shuffle(a) : a;
}

export default TrueFalseVisor;
